import { RecordItems } from "@/services/getRecordsLibrary";
import { checkAndClearCompletedRecording, RecordingData, saveRecordingDataToDB } from "@/utils/indexedDBUtils";
import { compareHash } from "@/utils/hashComparison";
import { RecordingCreationType } from "@/models/recording";
import { DerivedMergeFlags, MergeDerivedFlagsParams, RecordStatusEnum } from "../types";

const successStatus = [RecordStatusEnum.DONE, RecordStatusEnum.READY_TO_EDIT, RecordStatusEnum.READY_TO_EXPORT];

export const hasNewPauseStructure = (recordingData: RecordingData | undefined) => {
  if (!recordingData) return false;
  return (
    typeof recordingData.status === "number" &&
    typeof recordingData.duration === "string" &&
    recordingData.duration.length > 0 &&
    typeof recordingData.timestamp === "number" &&
    Number.isFinite(recordingData.timestamp) &&
    recordingData.timestamp > 0
  );
};

const hasFailed = (status: number | undefined) => status === RecordStatusEnum.FAILED;

export const getMergeDerivedFlags = ({ backendItem, localItem, localHasNewPauseStructure }: MergeDerivedFlagsParams): DerivedMergeFlags => {
  let isFailed = hasFailed(localItem?.status) || hasFailed(backendItem.status);
  const processingStatus = [6, 1];
  if (processingStatus.includes(backendItem.status)) {
    isFailed = false;
  }

  const isUploadRecording = backendItem.recordingType === RecordingCreationType.UPLOADED_RECORDING;

  // Uploads no longer need a local IndexedDB copy to detect a hash mismatch - the backend now
  // returns both its own computed hash and the one the frontend originally sent on every list
  // item, so the comparison works entirely off the API response (and across devices). Live
  // recordings still compare against the local IndexedDB copy, which is the only place their hash
  // is known.
  const isHashEqual = isUploadRecording
    ? compareHash(backendItem.audioHash, backendItem.audioHashFromFrontend)
    : compareHash(backendItem.audioHash, localItem?.audioHash);
  const isFailedDifferentHash = isFailed && isHashEqual === false;
  const isFailedNotSynced = isFailed && localItem?.isSync === false;
  const isOldData = localItem ? !localHasNewPauseStructure : false;
  const isOnIndexDB = Boolean(localItem);

  // Upload-type hash mismatches surface via the plain FAILED info tooltip (FailActions) instead
  // of the "upload interrupted" resolve modal that other interruption causes route to.
  const isUploadTypeHashMismatch = isFailedDifferentHash && isUploadRecording;
  const isUploadInterrupted = isOnIndexDB && (isOldData || isFailedNotSynced || (isFailedDifferentHash && !isUploadTypeHashMismatch));

  return {
    isOnIndexDB,
    isOldData,
    isHashEqual,
    isFailedDifferentHash,
    isUploadInterrupted,
  };
};

export const mergeLocalRecordings = (recordsList: RecordItems, allRecordingData: RecordingData[]): RecordItems => {
  const localById = new Map(allRecordingData.filter((recordingData) => Boolean(recordingData.recordingUUID))?.map((item) => [item.recordingUUID, item]));

  const mergedBackendItems = recordsList.items?.map((backendItem) => {
    const localItem = localById.get(backendItem.recordingId);
    const localHasNewPauseStructure = hasNewPauseStructure(localItem);

    const derivedFlags = getMergeDerivedFlags({
      backendItem,
      localItem,
      localHasNewPauseStructure,
    });

    const { isOnIndexDB } = derivedFlags;
    const isFailedSync = hasFailed(backendItem?.status) && !hasFailed(localItem?.status);
    const hasSuccessStatus = successStatus.includes(backendItem?.status);
    let status = localItem?.status ?? backendItem?.status;
    let isSync = localItem ? localItem.isSync : true;

    if (isFailedSync && isOnIndexDB) {
      status = RecordStatusEnum.FAILED;
      isSync = true;
      saveRecordingDataToDB(JSON.stringify({ ...localItem, status, isSync }));
    }

    if (backendItem?.status === RecordStatusEnum.IN_PROGRESS && localItem?.status === RecordStatusEnum.FAILED) {
      status = RecordStatusEnum.IN_PROGRESS;
      isSync = true;

      saveRecordingDataToDB(JSON.stringify({ ...localItem, status, isSync }));
    }

    if (backendItem?.status === RecordStatusEnum.RETRYING && localItem?.status === RecordStatusEnum.FAILED) {
      status = RecordStatusEnum.RETRYING;
      isSync = true;
      saveRecordingDataToDB(JSON.stringify({ ...localItem, status, isSync }));
    }

    if (backendItem?.status === RecordStatusEnum.PAUSED && localItem?.status === RecordStatusEnum.RECORDING) {
      status = RecordStatusEnum.PAUSED;
      isSync = true;
      saveRecordingDataToDB(JSON.stringify({ ...localItem, status, isSync }));
    }

    if (hasSuccessStatus && isOnIndexDB) {
      checkAndClearCompletedRecording(backendItem.recordingId);
    }

    return {
      ...backendItem,
      status,
      isSync,
      duration: localItem?.duration ?? backendItem.duration,
      ...derivedFlags,
    };
  });

  return {
    ...recordsList,
    items: mergedBackendItems,
  };
};
